/** `communityAcquisition` namespace — English (typed against the French source). */

import type { CommunityAcquisitionKeys } from './fr';

const communityAcquisition: CommunityAcquisitionKeys = {
  networks: {
    hackernews: 'Hacker News',
    bluesky: 'Bluesky',
    devto: 'Dev.to',
    reddit: 'Reddit',
    x: 'X (Twitter)',
    threads: 'Threads',
    instagram: 'Instagram',
    stackexchange: 'Stack Exchange',
    github: 'GitHub',
    mastodon: 'Mastodon',
    lemmy: 'Lemmy',
    discourse: 'Discourse forums',
    youtube_comments: 'YouTube (comments)',
    producthunt: 'Product Hunt',
    manual: 'Manual capture',
  },
  feedLink: 'Find conversations',
  catches: {
    title: 'Conversations to join',
    subtitle:
      'Public discussions where your expertise genuinely helps, found through official APIs (Hacker News, Bluesky, Dev.to, Reddit, Stack Exchange, GitHub, Mastodon, Lemmy, Discourse, YouTube, Product Hunt) or added by you (Facebook groups, LinkedIn…).',
    manualNotice:
      'Karexia never posts for you: copy the draft, open the thread and post it yourself. The disclosure line is on by default whenever your product is mentioned.',
    keywordsTitle: 'Tracked keywords',
    keywordsHint: 'Topics where you can offer useful help.',
    keywordPlaceholder: 'e.g. social media scheduler',
    keywordLabel: 'New keyword',
    addKeyword: 'Add',
    saveKeywords: 'Save keywords',
    keywordsCount: '{{count}}/{{limit}} keywords',
    keywordsUnlimited: '{{count}} keywords',
    keywordEnabled: 'On',
    keywordDisabled: 'Off',
    toggleKeyword: 'Turn “{{keyword}}” on / off',
    removeKeyword: 'Remove “{{keyword}}”',
    keywordsSaved: 'Keywords saved.',
    keywordLimitReached: 'Your plan limit is reached ({{used}}/{{limit}} keywords).',
    keywordDuplicate: 'This keyword is already tracked.',
    productTitle: 'Your product',
    productHint: 'Used for the disclosure line (“I’m the founder of …”).',
    productName: 'Product name',
    productUrl: 'Product URL',
    disclosureLang: 'Disclosure line language',
    disclosureLangEn: 'English',
    disclosureLangFr: 'French',
    retention: 'Threads are kept for {{days}} days, then deleted.',
    lastSweep: 'Last search {{when}}',
    saveProduct: 'Save product',
    productSaved: 'Product saved.',
    runSweep: 'Run search',
    sweeping: 'Searching…',
    sweepDone: 'Search done: {{inserted}} new conversations, {{updated}} updated.',
    noKeywords: 'Add at least one active keyword before running the search.',
    coverageTitle: 'Coverage by network',
    coverageCount: '{{count}} found',
    notConfigured: 'Not configured',
    coverageStatus: {
      ok: 'OK',
      skipped_no_key: 'Not configured',
      skipped_optional: 'Optional (inactive)',
      rate_limited: 'Rate limited',
      error: 'Error',
      unavailable: 'Unavailable',
      disabled: 'Disabled',
    },
    tabAll: 'All',
    statusFilter: 'Status',
    status: {
      open: 'To do',
      new: 'New',
      waiting: 'Waiting',
      done: 'Done',
      archived: 'Archived',
    },
    empty: 'No conversations for this filter.',
    emptyHint: 'Add keywords, then run the search.',
    loading: 'Loading conversations…',
    loadError: 'Could not load conversations.',
    unavailable: 'The conversation finder is not available on this environment yet.',
  },
  card: {
    hnHumanNote: 'Hacker News asks for human-written comments: use this draft as notes and write the reply in your own words.',
    score: 'Relevance {{score}}',
    by: 'by {{author}}',
    matched: 'Keyword: {{keyword}}',
    untitled: 'Untitled',
    mentionLevel: 'Product mention level',
    mentionNone: 'No mention',
    mentionSoft: 'Soft mention',
    mentionDirect: 'Direct mention',
    disclose: 'Add the disclosure line (“I’m the founder of {{product}}”)',
    productFallback: 'my product',
    generate: 'Generate a reply',
    generating: 'Generating…',
    draftLabel: 'Reply draft',
    draftSaved: 'Draft saved.',
    rewrite: 'Rewrite',
    rewriting: 'Rewriting…',
    rewriteCounter: '{{used}}/{{limit}} rewrites',
    rewriteUnlimited: '{{used}} rewrite(s)',
    rewriteLimit: 'Rewrite limit reached for your plan.',
    copy: 'Copy',
    copied: 'Reply copied — paste it into the thread yourself.',
    copyFailed: 'Could not copy — select the text manually.',
    openThread: 'Open thread',
    markDone: 'Mark as done',
    archive: 'Archive',
    reopen: 'Reopen',
    rules: 'Community rules',
    degraded: 'AI unavailable — fallback draft, rework it before posting.',
    disclosureIncluded: 'Disclosure line included',
    pacingPaused: 'Account paused: {{reason}}',
    pacingExhausted: 'Daily allowance reached ({{used}}/{{limit}} replies) for your {{network}} account.',
    pacingExplain:
      'Pacing protects your account’s reputation and the community’s quality: come back tomorrow.',
    pacingLink: 'View account health',
    pacingToday: 'Today: {{used}}/{{limit}} replies',
    pacingUndeclared: '{{network}} account not declared — declare it to get suitable pacing.',
    mentionNotAllowed:
      'At your current level on this network, mentioning the product is not recommended yet: participate without promotion first.',
    actionError: 'The action failed — try again.',
  },
  pauseReasons: {
    removal_flag: 'content removal flagged',
    follower_drop: 'follower drop above 10%',
  },
  rules: {
    title: 'Rules — {{community}} ({{network}})',
    loading: 'Loading rules…',
    summary: 'Summary (promotion and links)',
    rulesList: 'Rules',
    noRules: 'No rules retrieved for this community.',
    about: 'About',
    subscribers: '{{count}} members',
    sourceStatus: {
      ok: 'Official API',
      skipped_no_key: 'API not configured',
      error: 'Source error',
      not_found: 'Community not found',
      curated: 'Summarised official guidelines',
    },
    summarySource: {
      llm: 'AI summary',
      extracted: 'Extracted from the rules',
      curated: 'Summary of the official guidelines',
      none: 'No summary',
    },
    notes: 'Your notes',
    notesPlaceholder: 'What you keep in mind to participate properly here…',
    saveNotes: 'Save notes',
    notesSaved: 'Notes saved.',
    refresh: 'Refresh rules',
    loadError: 'Could not load the rules.',
  },
  finder: {
    tabs: {
      conversations: 'Conversations',
      problems: 'Top problems this week',
      sources: 'Sources',
    },
    manualBadge: 'Added manually',
    community: 'Community: {{community}}',
    sources: {
      title: 'Conversation sources',
      intro: 'Every source is optional: turn on the ones you care about. A source with no key or no setting simply stays inactive — that is not an error. Karexia only uses official APIs and never crawls Facebook, LinkedIn, Instagram or X.',
      enabledCount: '{{count}}/{{limit}} sources enabled',
      enabledUnlimited: '{{count}} sources enabled (unlimited)',
      requirement: {
        none: 'No key needed',
        api_key: 'API key required',
        config: 'Needs setup',
      },
      state: {
        active: 'Active',
        off: 'Off',
        optional_inactive: 'Optional · inactive',
        needs_config: 'Needs setup',
        unavailable: 'Unavailable',
        unknown: 'Unknown state',
      },
      toggle: 'Enable {{source}}',
      save: 'Save',
      saved: 'Source saved.',
      error: 'Saving failed — try again.',
      loadError: 'Could not load the sources.',
      limitReached: 'Your plan allows {{limit}} enabled sources ({{used}} already active). Turn one off or upgrade your plan.',
      overLimit: 'Beyond your plan: this source is skipped during searches (turn it off to free the slot).',
      instancesLabel: 'Instances (one per line)',
      instancesHint: 'E.g. mastodon.social. Public https domains only; each instance’s robots.txt is respected.',
      forumsLabel: 'Discourse forums (one address per line)',
      forumsHint: 'E.g. meta.discourse.org. Only /search.json and /latest.json are read, robots.txt is respected, and the source stops immediately on 403/429.',
      sitesLabel: 'Stack Exchange sites',
      sitesHint: 'E.g. stackoverflow, serverfault, webmasters. Default: stackoverflow.',
      invalidHosts: 'Rejected addresses: {{hosts}} (https only, public domain).',
      invalidSites: 'Rejected sites: {{hosts}}.',
      tooMany: 'Too many entries for this source.',
      activateReddit: 'Reddit: activate (needs API app)',
      activateGeneric: '{{source}}: activate (needs API key)',
      activateKeys: 'To be added by the platform administrator: {{keys}}',
      githubHint: 'Connect GitHub in Settings › Integrations for a higher quota and Discussions search (otherwise: Issues only, low anonymous quota).',
      stackexchangeHint: 'An optional app key (STACKEXCHANGE_KEY) raises the daily quota.',
      lastRun: 'Last search: {{status}} · {{count}}',
      captureUsage: 'Manual captures: {{used}}/{{limit}} this month',
      captureUsageUnlimited: 'Manual captures: {{used}} this month (unlimited)',
      managed: 'Custom settings saved.',
      defaults: 'Default settings: the four original sources are active. Change a source to customise them.',
    },
    capture: {
      title: 'Add a conversation',
      intro: 'Facebook and LinkedIn no longer allow automated reading of their groups. Paste the text of a post yourself (or select it with the "Send to Karexia" browser extension): Karexia extracts the problem, scores the fit with your product brief and prepares a reply draft. Nothing is read or posted automatically.',
      textLabel: 'Post text',
      textPlaceholder: 'Paste the text of the post or question here…',
      urlLabel: 'Post address (optional)',
      platformLabel: 'Platform',
      platformAuto: 'Detect from the address',
      groupLabel: 'Group or community (optional)',
      productLabel: 'Product this conversation is for',
      groupPlaceholder: 'e.g. Montreal real estate investors',
      submit: 'Add to the queue',
      submitting: 'Analysing…',
      charCount: '{{count}}/{{max}} characters',
      created: 'Conversation added to your queue.',
      duplicate: 'This conversation is already in your queue — no capture used.',
      degraded: 'AI analysis is unavailable: the conversation is saved, generate the draft from its card.',
      problem: 'Problem detected',
      fit: 'Fit with your brief: {{score}}/100',
      draftReady: 'A reply draft is ready below — copy it and post it yourself.',
      limitReached: 'Monthly capture limit reached ({{used}}/{{limit}}). Upgrade your plan or wait until next month.',
      tooLong: 'The text exceeds {{max}} characters: paste a single post.',
      tooShort: 'The text is too short (minimum {{min}} characters).',
      invalidUrl: 'The address must start with http:// or https://.',
      error: 'Adding failed — try again.',
      platforms: {
        facebook: 'Facebook (group)',
        linkedin: 'LinkedIn (group)',
        instagram: 'Instagram',
        x: 'X',
        reddit: 'Reddit',
        slack: 'Slack',
        discord: 'Discord',
        whatsapp: 'WhatsApp',
        telegram: 'Telegram',
        forum: 'Forum',
        other: 'Other',
      },
    },
    problems: {
      title: 'Top problems this week',
      intro: 'The questions that keep coming back, grouped by community and source. Each group suggests content ideas — you stay in control: nothing is published automatically.',
      window: 'Last {{days}} days',
      analysed: '{{analysed}} conversations analysed, {{clustered}} grouped',
      empty: 'No recurring problem yet.',
      emptyHint: 'Run a search or add conversations: a problem appears as soon as two or more threads from the same community look alike.',
      count: '{{count}} conversations',
      lastSeen: 'Latest: {{when}}',
      engagement: 'Engagement: {{count}}',
      fit: 'Average fit: {{score}}/100',
      samples: 'Examples',
      ideas: 'Content ideas',
      idea: {
        guide: 'Guide: {{topic}}',
        faq: 'FAQ: {{topic}}',
        checklist: 'Checklist: {{topic}}',
        comparison: 'Comparison: {{topic}}',
      },
      openComposer: 'Open in the composer',
      ideaNote: 'The outline opens in the composer as an editable draft: nothing is published automatically.',
      openThread: 'Open thread',
      loadError: 'Could not load the problems.',
      refresh: 'Refresh',
    },
  },
  health: {
    tab: 'Account health',
    title: 'Account health & pacing',
    subtitle:
      'One level per account, computed from official read APIs or your own declarations, which sets a reasonable daily pace.',
    principle1: 'Be transparent: say you are the founder when you mention your product.',
    principle2: 'Follow each community’s rules before participating.',
    principle3: 'Offer real help: most of your replies should promote nothing.',
    loading: 'Loading…',
    empty: 'No account declared. Add your accounts to get suitable pacing.',
    addTitle: 'Declare an account',
    addSelect: 'Network',
    addButton: 'Add this account',
    allDeclared: 'Every network is declared.',
    levelLine: 'Level: {{level}}',
    levels: {
      newcomer: 'Newcomer',
      regular: 'Regular',
      trusted: 'Trusted',
      insider: 'Insider',
    },
    scoreLabel: 'Health score {{score}} out of 10',
    scoreCaption: '/10',
    todayPill: 'Today: {{used}}/{{limit}} replies',
    todayUnknown: 'Today’s pace unavailable',
    allowancesTitle: 'Daily allowance',
    repliesPerDay: '{{count}} replies / day',
    postsPerDay: '{{count}} posts / day',
    linksAllowed: 'Links allowed when they help',
    linksNotAllowed: 'No links for now',
    mentionAllowed: 'Product mention possible (with disclosure)',
    mentionNotAllowed: 'No product mention yet',
    paused: 'Paused: {{reason}}',
    pausedHint:
      'Drafts and today’s moves for this account are paused. Re-read the community rules, then re-audit.',
    source: {
      api: 'Official API',
      self_reported: 'Self-reported',
      mixed: 'API + self-reported',
    },
    apiStatus: {
      ok: 'API connected',
      not_connected: 'Account not connected',
      skipped_no_key: 'API not configured',
      error: 'API error',
      not_available: 'No official API',
      not_found: 'Profile not found',
      self_reported_only: 'Self-reported only',
    },
    fixesTitle: 'Recommended improvements',
    fixes: {
      removal_review: 'Re-read the community rules and the removal notice before resuming.',
      bio: 'Clear bio: who you are and what you are building.',
      avatar: 'Add a profile picture.',
      pinned_post: 'Pin a post that honestly presents your project.',
      contribute_first: 'Contribute first: helpful replies, with no link and no product mention.',
      connect_api: 'Connect the account for official measurements.',
    },
    signalsTitle: 'Measured signals',
    signals: {
      followers: 'Followers',
      following: 'Following',
      karma: 'Karma',
      posts_count: 'Posts',
      account_age_days: 'Account age (days)',
      follower_drop_pct: 'Follower drop (%)',
      removal_flag: 'Removal flagged',
    },
    yes: 'yes',
    no: 'no',
    history: 'History',
    hideHistory: 'Hide history',
    historyEmpty: 'No history.',
    trigger: {
      audit: 'Audit',
      self_report: 'Declaration',
    },
    reaudit: 'Re-audit',
    reauditing: 'Auditing…',
    reaudited: 'Audit done.',
    selfReportTitle: 'Manual declaration',
    handle: 'Handle',
    accountAge: 'Account age (days)',
    karma: 'Karma / reputation',
    followers: 'Followers',
    posts: 'Posts',
    hasBio: 'Bio filled in',
    hasAvatar: 'Profile picture',
    hasPinned: 'Pinned post',
    removalFlag: 'Content was recently removed by moderators',
    saveSelfReport: 'Save declaration',
    saved: 'Level recomputed.',
    remove: 'Remove this account',
    removed: 'Account removed.',
    measuredAt: 'Measured {{when}}',
    error: 'The action failed — try again.',
    loadError: 'Could not load account health.',
    unavailable: 'Account health is not available on this environment yet.',
  },
};

export default communityAcquisition;
